<template>
  <div>
    <div class="container">
      <div class="container">

        <!-- LOADER -->
        <div v-if="isLoading" class="loader-wrap">
          <div class="loader"></div>
        </div>

        <!-- CONTENT -->
        <template v-else>
          <!-- PAGE HEADER -->
          <div class="section-head" :style="{ '--accent': '#4F7EB3' }">
            <div class="head-left">
              <h3 class="title">{{ $t('streams.external.title') }}</h3>
            </div>

            <div class="head-actions">
              <button type="button" class="btn-outline" @click="backHome">
                <i class="bi bi-house-door-fill"></i>
                {{ $t('streams.external.home') }}
              </button>
            </div>
          </div>

          <!-- BODY CARD -->
          <div class="card page-card">
            <div class="card-body">

              <!-- WEATHER BLOCK -->
              <div class="card block-card kpi-accent" :style="{ '--accent':'#4F7EB3' }">
                <div class="block-head">
                  <i class="bi bi-cloud-sun block-icon"></i>
                  <h6 class="block-title">{{ $t('streams.external.weatherTitle') }}</h6>
                </div>

                <div class="block-body">
                  <!-- EMPTY STATE -->
                  <div
                    v-if="!apiweatherdataStore.apiweatherdatarecords.length && !isDeleting"
                    class="empty-card"
                  >
                    <div class="empty-cta" v-if="!isSmallScreen">
                      <router-link
                        :to="userStore.isRegularUser ? null : { path: '/connect_api', query: { type: 'Weather Data' } }"
                        class="btn-solid ok big-square"
                        :class="{ 'disabled-router-link': userStore.isRegularUser }"
                      >
                        <i class="bi bi-plus-lg"></i>
                      </router-link>

                      <div class="empty-text">
                        <p class="empty-title">{{ $t('streams.external.emptyTitle') }}</p>
                        <p v-if="!userStore.isRegularUser" class="empty-sub"><b>{{ $t('streams.external.createFirst') }}</b></p>
                        <p v-else class="empty-sub"><b>{{ $t('streams.external.askAdmin') }}</b></p>
                      </div>
                    </div>

                    <div class="empty-cta" v-else>
                      <div class="empty-text">
                        <p class="empty-title">{{ $t('streams.external.emptyTitle') }}</p>
                        <div v-if="!userStore.isRegularUser">
                          <p class="empty-sub"><b>{{ $t('streams.external.createFirst') }}</b></p>
                          <p class="empty-sub"><b>{{ $t('streams.external.useLargerScreen') }}</b></p>
                        </div>
                        <p v-else class="empty-sub"><b>{{ $t('streams.external.askAdmin') }}</b></p>
                      </div>
                    </div>
                  </div>

                  <!-- LIST -->
                  <div v-else class="conn-grid">
                    <button
                      v-for="apiweatherdatarecord in apiweatherdataStore.apiweatherdatarecords"
                      :key="apiweatherdatarecord.APIWeatherRecordId"
                      type="button"
                      class="conn-pill"
                      :class="{ selected: selectedWeatherDataRecordId === apiweatherdatarecord.APIWeatherRecordId, disabled: userStore.isRegularUser }"
                      @click="selectWeatherDataConnection(apiweatherdatarecord.APIWeatherRecordId)"
                      :disabled="userStore.isRegularUser"
                    >
                      <i class="bi bi-cloud-sun"></i>
                      <span>{{ apiweatherdatarecord.APIWeatherRecordName }}</span>
                    </button>
                  </div>
                </div>

                <div class="block-footer">
                  <router-link
                    v-if="!isSmallScreen"
                    :to="userStore.isRegularUser ? null : { path: '/connect_api', query: { type: 'Weather Data' } }"
                    class="btn-outline"
                    :class="{ 'disabled-router-link': userStore.isRegularUser }"
                  >
                    <i class="bi bi-plus-lg"></i>
                    {{ $t('streams.external.add') }}
                  </router-link>

                  <button
                    type="button"
                    class="btn-solid danger"
                    @click="deleteSelectedWeatherDataConnection"
                    :disabled="!selectedWeatherDataRecordId || userStore.isRegularUser"
                  >
                    <i class="bi bi-trash"></i>
                    {{ $t('streams.external.deleteSelected') }}
                  </button>
                </div>
              </div>
              <!-- /WEATHER BLOCK -->

            </div>

            <div class="footer-actions">
              <button type="button" class="btn-outline" @click="backHome">
                <i class="bi bi-house-door-fill"></i>
                {{ $t('streams.external.backHome') }}
              </button>
            </div>
          </div>
          <!-- /BODY CARD -->
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import { RouterLink } from 'vue-router'

import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

import { useUserStore } from '@/stores/UserStore'
import { useAPIWeatherDataStore } from '@/stores/APIWeatherDataStore'

export default {
  components: { RouterLink },

  setup() {
      const toast = useToast()
      const { t } = useI18n()

      const userStore = useUserStore()
      const apiweatherdataStore = useAPIWeatherDataStore()

      const router = useRouter()

      const userRole = ref('')
      const selectedWeatherDataRecordId = ref(null)

      const isSmallScreen = ref(window.innerWidth <= 768)

      const isLoading = ref(null)
      const isDeleting = ref(false)

      const handleResize = () => {
        isSmallScreen.value = window.innerWidth <= 768
      }

      onBeforeMount(async () => {
        isLoading.value = true
        await apiweatherdataStore.fetchAPIWeatherDataRecords()
        isLoading.value = false
        userRole.value = userStore.currentCompany.role

        window.addEventListener('resize', handleResize)
        handleResize() // Set initial state based on current window size
      })

      const selectWeatherDataConnection = (id) => {
        if (selectedWeatherDataRecordId.value === id) {
          selectedWeatherDataRecordId.value = null
        } else {
          selectedWeatherDataRecordId.value = id
        }
      }

      const deleteSelectedWeatherDataConnection = async () => {
          if (confirm(t('streams.external.confirmDelete'))) {
            isDeleting.value = true
            await apiweatherdataStore.deleteAPIWeatherDataRecord(selectedWeatherDataRecordId.value)
            selectedWeatherDataRecordId.value = null
            await apiweatherdataStore.fetchAPIWeatherDataRecords()
            isDeleting.value = false
            toast.success(t('streams.external.deleted'))
          }
      }

      const backHome = () => {
            router.push('/')
        }

      onBeforeUnmount(() => {
        // Remove the resize event listener when component is unmounted
        window.removeEventListener('resize', handleResize)
    })

      return { 
        userStore,
        apiweatherdataStore,
        userRole,
        isSmallScreen,
        isLoading,
        isDeleting,
        selectedWeatherDataRecordId,
        selectWeatherDataConnection,
        deleteSelectedWeatherDataConnection,
        backHome
      }
    }
}
</script>

<style scoped>
/* ---------- Loader ---------- */
.loader-wrap{
  position:relative; min-height:60vh; display:flex; align-items:center; justify-content:center;
}
.loader{
  width:80px; padding:8px; aspect-ratio:1; border-radius:50%;
  background:#B2C149;
  --_m: conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spinner 1s linear infinite;
}
@keyframes spinner{ to{ transform:rotate(1turn) } }

/* ---------- Header ---------- */
.section-head{
  display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap;
  border-left:6px solid var(--accent,#4F7EB3);
  background:#fff; border:1px solid #e6edf3; border-radius:12px;
  padding:12px 14px; margin:10px 0 14px;
  box-shadow:0 3px 12px rgba(13,38,76,.06);
}
.title{ margin:0; font-weight:800; color:#425461 }
.head-left{ display:flex; align-items:center; gap:10px }
.head-actions{ display:flex; gap:10px; flex-wrap:wrap }

/* ---------- Buttons ---------- */
.btn-outline{
  display:inline-flex; align-items:center; gap:8px;
  border:1px solid #4F7EB3; color:#4F7EB3; background:#fff;
  padding:8px 12px; border-radius:10px; cursor:pointer; text-decoration:none;
}
.btn-outline:hover{ background:#4F7EB3; color:#fff }

.btn-solid{
  display:inline-flex; align-items:center; gap:8px;
  border:0; color:#fff; padding:8px 12px; border-radius:10px; cursor:pointer;
  background:#6C9046;
}
.btn-solid.ok{ background:#6C9046 }
.btn-solid.danger{ background:#C21500 }
.btn-solid:disabled{ opacity:.6; cursor:not-allowed }

/* Big plus tile for empty state */
.big-square{
  width:72px; height:72px; border-radius:14px; justify-content:center;
  font-size:22px;
}

/* ---------- Page Card ---------- */
.page-card{ border:none; box-shadow:0 4px 18px rgba(13,38,76,.06); border-radius:14px }
.footer-actions{ display:flex; justify-content:flex-end; gap:10px; margin-bottom: 20px;}

/* ---------- Block Card with accent spine ---------- */
.block-card{
  position:relative; border:none; border-radius:14px;
  box-shadow:0 3px 12px rgba(13,38,76,.06);
  margin-bottom:18px; padding:10px 12px;
}
.kpi-accent::before{
  content:""; position:absolute; inset:0 auto 0 0; width:6px; background:var(--accent,#4F7EB3);
  border-radius:14px 0 0 14px;
}
.block-head{ display:flex; align-items:center; gap:10px; padding:6px 4px }
.block-icon{ font-size:1.2rem; color:#4F7EB3 }
.block-title{ margin:0; font-weight:800; color:#425461 }
.block-body{ padding:6px 4px 10px }

/* ---------- Empty State ---------- */
.empty-card{
  border:1px dashed #e6edf3; border-radius:12px; padding:16px; background:#fafcfe;
  display:flex; align-items:center; justify-content:center;
}
.empty-cta{ display:flex; align-items:center; gap:16px; flex-wrap:wrap }
.empty-text{ display:flex; flex-direction:column; gap:4px }
.empty-title{ margin:0; font-weight:700; color:#4F7EB3 }
.empty-sub{ margin:0; color:#667684 }

/* ---------- Connections grid & pills ---------- */
.conn-grid{
  display:grid; grid-template-columns:repeat(auto-fit, minmax(240px, 1fr));
  gap:12px; margin-top:4px;
}
.conn-pill{
  display:flex; align-items:center; gap:10px;
  background:#fff; color:#4F7EB3; border:1px solid #4F7EB3;
  border-radius:12px; padding:10px 12px; height:52px;
  box-shadow:0 2px 8px rgba(13,38,76,.04);
  cursor:pointer; text-align:left;
}
.conn-pill i{ font-size:1.1rem }
.conn-pill.selected{ background:#4F7EB3; color:#fff; border-color:#4F7EB3 }
.conn-pill.disabled{ cursor:not-allowed; opacity:.65 }

/* ---------- Block footer ---------- */
.block-footer{
  display:flex; gap:10px; justify-content:flex-end; padding:6px 4px 2px;
}

/* ---------- Disabled links ---------- */
.disabled-router-link{
  pointer-events:none; cursor:not-allowed; opacity:.6;
}

/* ---------- Responsive tweaks ---------- */
@media (max-width: 600px){
  .title{ font-size:1.1rem }
  .conn-grid{ grid-template-columns:repeat(auto-fit, minmax(200px, 1fr)) }
}
</style>